import React, { useMemo, useState } from "react";
import { message } from "antd";
import DynamicBillSummaryPanel from "../components/common/DynamicBillSummaryPanel";
import { useCanConfigureBoq } from "./useCanConfigureBoq";
import type { DynamicBillSummaryTab } from "../types/tender.types";
import type { ItemLibraryItem } from "../api/itemLibrary";
import type { PlotMatrixCategoryColumn } from "../utils/plotMatrixCategoryGenerator";
import type { DynamicBillSummaryLineItem } from "../utils/dynamicBillSummarySerialization";

export interface DynamicBillSummaryTabDescriptor {
  key: string;
  label: string;
  isDynamic: true;
  children: React.ReactNode;
}

interface UseDynamicBillSummaryOptions {
  /** Persisted tab array — e.g. `state.groundworks?.dynamicBillSummaryTabs`. */
  tabs: DynamicBillSummaryTab[] | undefined;
  /** Called with the full updated array on every add/rename/delete/edit. */
  onTabsChange: (tabs: DynamicBillSummaryTab[]) => void;
  isViewMode?: boolean;
  itemLibraryData?: ItemLibraryItem[];
  /** Fired with the new tab's id right after `handleAddTab` creates it — use
   *  to switch the page's own "active tab" state to the freshly-created tab. */
  onTabCreated?: (id: string) => void;
  /** Live Plot Matrix columns (`generatePlotMatrixCategoryColumns` on the
   *  current groundworks data) — threaded through to DynamicBillSummaryPanel's
   *  Valuation Stage lookup so it only ever offers stages that are real,
   *  currently-generated Plot Matrix columns. Only matters in dev mode. */
  plotMatrixColumns?: PlotMatrixCategoryColumn[];
  /** Whether to offer the "Run Matrix Mapping" section — the Roads & Sewers
   *  counterpart of `plotMatrixColumns` above. See DynamicBillSummaryPanel's
   *  own `enableRunMatrixMapping` prop. */
  enableRunMatrixMapping?: boolean;
  /** Persisted line items per tab id (from the boq-versions API) — threaded
   *  through to each DynamicBillSummaryPanel's SummaryKey column. Only
   *  matters in dev mode. */
  apiLineItemsByTabId?: Map<string, Map<string, DynamicBillSummaryLineItem>>;
  /** OH&P/Preliminaries markup factor — threaded straight through to
   *  DynamicBillSummaryPanel's own `markupFactor` prop (see there). */
  markupFactor?: number;
  /** Labels already in use by the page's own fixed (non-dynamic) Bill
   *  Summary tabs — e.g. "Sum Analysis", "Cut & Fill", "Foundations" on
   *  `/groundworks/review`. A new/renamed dynamic tab can't take one of
   *  these names, checked separately from (and before) the dynamic-tab
   *  duplicate check below so the two cases can show distinct messages. */
  reservedLabels?: string[];
}

/**
 * Reusable Dynamic Bill Summary state: add/rename/delete tabs (each holding a
 * nested Section/Item tree with an item-library code lookup), plus
 * ready-to-render tab descriptors wrapping `DynamicBillSummaryPanel`.
 *
 * Gated by `useCanConfigureBoq()` (hooks/useCanConfigureBoq.ts) — currently
 * backed by the runtime "Configure BOQ" switch next to the Re-calculate
 * button on /groundworks/review (off by default, per browser via
 * localStorage; see contexts/ConfigureBoqContext.tsx), independent of Dev
 * Mode. When off, existing tabs and their contents stay
 * fully visible (read-only, same as `isViewMode`) — only the "+ Add Tab"
 * control, each tab's own rename/delete icons, and every tab's own Add
 * Section/Add Item/Edit/Delete Actions column hide. Nothing is cleared and
 * no save happens purely from flipping the switch — it only toggles what's
 * editable right now.
 *
 * The caller owns: where the "+ Add Tab" control and per-tab rename/delete UI
 * render (they're woven into the page's own existing tab bar), and merging
 * `tabDescriptors` into whatever other fixed tabs it shows. This hook owns
 * everything else — the tab-array CRUD and the "Add Tab"/rename modal's state.
 *
 * Usage (see GroundworksReview.tsx):
 *   const dynamicBillSummary = useDynamicBillSummary({
 *     tabs: state.groundworks?.dynamicBillSummaryTabs,
 *     onTabsChange: (tabs) =>
 *       updateField("groundworks", { ...state.groundworks, dynamicBillSummaryTabs: tabs }),
 *     isViewMode: state.isViewMode,
 *     itemLibraryData,
 *     onTabCreated: (id) => setActiveBillTab(id),
 *   });
 *   // merge into the tab list: tabs.push(...dynamicBillSummary.tabDescriptors)
 *   // render dynamicBillSummary.addTabModalVisible / .setAddTabModalVisible etc.
 *   //   for the "+" control's modal, .handleDeleteTab for each dynamic tab's
 *   //   Delete icon, and .renameTabId / .renameTabLabel / .openRenameModal /
 *   //   .closeRenameModal / .commitRenameModal for its Edit icon — sharing the
 *   //   same "Add Bill Summary" modal's markup with handleAddTab.
 */
export function useDynamicBillSummary({
  tabs: rawTabs,
  onTabsChange,
  isViewMode,
  itemLibraryData,
  onTabCreated,
  plotMatrixColumns,
  enableRunMatrixMapping,
  apiLineItemsByTabId,
  markupFactor,
  reservedLabels = [],
}: UseDynamicBillSummaryOptions) {
  const enabled = useCanConfigureBoq();
  const tabs = rawTabs ?? [];

  const [addTabModalVisible, setAddTabModalVisible] = useState(false);
  const [newTabName, setNewTabName] = useState("");

  // Rename — triggered by an Edit icon next to each dynamic tab's Delete
  // icon, sharing the "Add Bill Summary" modal's markup with handleAddTab.
  const [renameTabId, setRenameTabId] = useState<string | null>(null);
  const [renameTabLabel, setRenameTabLabel] = useState("");

  const openRenameModal = (id: string, currentLabel: string) => {
    setRenameTabId(id);
    setRenameTabLabel(currentLabel);
  };

  const closeRenameModal = () => {
    setRenameTabId(null);
    setRenameTabLabel("");
  };

  // Case-insensitive, trimmed name-collision check — shared by both
  // handleAddTab and commitRenameModal below (`excludeId` lets a rename
  // match its own current name without tripping the check on itself).
  const isDuplicateTabName = (label: string, excludeId: string | null): boolean => {
    const normalized = label.trim().toLowerCase();
    return tabs.some((t) => t.id !== excludeId && t.label.trim().toLowerCase() === normalized);
  };

  // Case-insensitive, trimmed check against the page's own fixed tab labels
  // (e.g. "Sum Analysis", "Cut & Fill") — kept separate from the dynamic-tab
  // duplicate check above so the two cases can show distinct messages: a
  // fixed tab's name is permanently off-limits, not just "already taken".
  const isReservedTabName = (label: string): boolean => {
    const normalized = label.trim().toLowerCase();
    return reservedLabels.some((l) => l.trim().toLowerCase() === normalized);
  };

  // Live flags for whatever's currently typed in the Add/Rename modal — let
  // the caller (GroundworksReview.tsx) disable the OK button and show inline
  // feedback proactively, in addition to the hard block + alert below.
  const currentTabNameDraft = renameTabId ? renameTabLabel : newTabName;
  const isCurrentTabNameDuplicate = isDuplicateTabName(currentTabNameDraft, renameTabId);
  const isCurrentTabNameReserved = isReservedTabName(currentTabNameDraft);

  const commitRenameModal = () => {
    if (!renameTabId) return;
    const label = renameTabLabel.trim();
    if (label) {
      if (isReservedTabName(label)) {
        message.warning(`"${label}" is a default Bill Summary tab name and can't be used here.`);
        return;
      }
      if (isDuplicateTabName(label, renameTabId)) {
        message.warning(`A Bill Summary tab named "${label}" already exists.`);
        return;
      }
      onTabsChange(tabs.map((t) => (t.id === renameTabId ? { ...t, label } : t)));
    }
    closeRenameModal();
  };

  const handleAddTab = () => {
    const label = newTabName.trim();
    if (!label) return;
    if (isReservedTabName(label)) {
      message.warning(`"${label}" is a default Bill Summary tab name and can't be used here.`);
      return;
    }
    if (isDuplicateTabName(label, null)) {
      message.warning(`A Bill Summary tab named "${label}" already exists.`);
      return;
    }
    const id = `dynamic-${Date.now()}`;
    onTabsChange([...tabs, { id, label, nodes: [] }]);
    setNewTabName("");
    setAddTabModalVisible(false);
    onTabCreated?.(id);
  };

  const handleDeleteTab = (id: string) => {
    onTabsChange(tabs.filter((t) => t.id !== id));
  };

  const tabDescriptors = useMemo<DynamicBillSummaryTabDescriptor[]>(
    () =>
      tabs.map((tab) => ({
        key: tab.id,
        label: tab.label,
        isDynamic: true as const,
        children: (
          <DynamicBillSummaryPanel
            tab={tab}
            isViewMode={isViewMode}
            configureBoqEnabled={enabled}
            itemLibraryData={itemLibraryData}
            plotMatrixColumns={plotMatrixColumns}
            enableRunMatrixMapping={enableRunMatrixMapping}
            apiLineItemsById={apiLineItemsByTabId?.get(tab.id)}
            markupFactor={markupFactor}
            onChange={(updated) => onTabsChange(tabs.map((t) => (t.id === updated.id ? updated : t)))}
          />
        ),
      })),
    [
      tabs,
      isViewMode,
      enabled,
      itemLibraryData,
      onTabsChange,
      plotMatrixColumns,
      enableRunMatrixMapping,
      apiLineItemsByTabId,
      markupFactor,
    ],
  );

  return {
    enabled,
    tabs,
    tabDescriptors,
    addTabModalVisible,
    setAddTabModalVisible,
    newTabName,
    setNewTabName,
    handleAddTab,
    handleDeleteTab,
    renameTabId,
    renameTabLabel,
    setRenameTabLabel,
    openRenameModal,
    closeRenameModal,
    commitRenameModal,
    isCurrentTabNameDuplicate,
    isCurrentTabNameReserved,
  };
}
